import { OFFER_TYPES } from "../constants/offerTypes";
import { findPortalDataEntity, pickPortalDataEntity } from "./portalData";
import { MAIN_SELECTION_ENTITIES } from "../constants/mainSelectionEntities";

function phoneGroupIsEqual(phone_1, phone_2) {
  return (
    phone_1.baseName === phone_2.baseName &&
    phone_1.brand.id === phone_2.brand.id &&
    phone_1.simCount === phone_2.simCount &&
    phone_1.state.id === phone_2.state.id
  );
}

function phoneIdsOfSameGroup(portalData, phoneIds) {
  if (phoneIds.length === 0) return false;

  const controlPhone = pickPortalDataEntity(portalData, "phone", phoneIds);

  if (controlPhone == null) {
    return undefined;
  }

  for (const id of phoneIds) {
    const phone = findPortalDataEntity(portalData, "phone", id);
    if (phone != null) {
      if (!phoneGroupIsEqual(controlPhone, phone)) {
        return false;
      }
    }
  }
  return true;
}

function phoneVariantIdsOfSamePhone(portalData, phoneVariantIds) {
  if (phoneVariantIds.length === 0) return false;

  const controlPhoneVariant = pickPortalDataEntity(
    portalData,
    "phoneVariant",
    phoneVariantIds
  );

  if (controlPhoneVariant == null) {
    return undefined;
  }

  for (const id of phoneVariantIds) {
    const phoneVariant = findPortalDataEntity(portalData, "phoneVariant", id);
    if (phoneVariant != null) {
      if (controlPhoneVariant.phone.id !== phoneVariant.phone.id) {
        return false;
      }
    }
  }
  return true;
}

function phoneVariantIdsOfSamePhoneGroup(portalData, phoneVariantIds) {
  if (phoneVariantIds.length === 0) return false;

  const controlPhoneVariant = pickPortalDataEntity(
    portalData,
    "phoneVariant",
    phoneVariantIds
  );

  if (controlPhoneVariant == null) {
    return undefined;
  }

  const controlPhone = findPortalDataEntity(
    portalData,
    "phone",
    controlPhoneVariant.phone.id
  );
  for (const id of phoneVariantIds) {
    const phoneVariant = findPortalDataEntity(portalData, "phoneVariant", id);
    if (phoneVariant != null) {
      const phone = findPortalDataEntity(
        portalData,
        "phone",
        phoneVariant.phone.id
      );
      if (!phoneGroupIsEqual(controlPhone, phone)) {
        return false;
      }
    }
  }
  return true;
}

function phonesFromPhoneIds(portalData, offerType, phoneIds) {
  const offerTypePhoneIds = portalData.entities.phones[`${offerType}Ids`];
  let phones = [];

  for (const id of phoneIds) {
    if (offerTypePhoneIds.indexOf(id) >= 0) {
      const phone = findPortalDataEntity(portalData, "phone", id);
      if (phone != null) {
        phones.push(phone);
      }
    }
  }

  return phones;
}

function phonesFromPhoneVariantIds(portalData, offerType, phoneVariantIds) {
  const offerTypePhoneIds = portalData.entities.phones[`${offerType}Ids`];
  let phones = [];

  for (const id of phoneVariantIds) {
    const phoneVariant = findPortalDataEntity(portalData, "phoneVariant", id);
    if (
      phoneVariant != null &&
      offerTypePhoneIds.indexOf(phoneVariant.phone.id) >= 0
    ) {
      const phone = findPortalDataEntity(
        portalData,
        "phone",
        phoneVariant.phone.id
      );
      phones.push(phone);
    }
  }

  return phones;
}

const allPhonesByGroup = (portalData, offerType, basePhone) => {
  try {
    let phones = [];
    for (const id of portalData.entities.phones[`${offerType}Ids`]) {
      const phone = findPortalDataEntity(portalData, "phone", id);
      if (phoneGroupIsEqual(basePhone, phone)) {
        phones.push(phone);
      }
    }

    return phones;
  } catch (e) {
    return [basePhone];
  }
};

const bestPhoneFromPhoneGroup = (portalData, offerType, filter) => {
  try {
    let phones;

    if (filter.phoneVariantId.length > 0) {
      phones = phonesFromPhoneVariantIds(
        portalData,
        offerType,
        filter.phoneVariantId
      );
    } else {
      phones = phonesFromPhoneIds(portalData, offerType, filter.phoneId);
    }

    let phone = phones[0];
    for (const currentPhone of phones) {
      if (currentPhone.memory < phone.memory) {
        phone = currentPhone;
      }
    }

    return phone;
  } catch (e) {
    return undefined;
  }
};

const multiPhoneGroupsForOfferType = (portalData, offerType) => {
  const phoneIds = portalData.entities.phones[`${offerType}Ids`];

  try {
    if (phoneIds.length === 0) {
      return undefined;
    } else {
      const sameGroup = phoneIdsOfSameGroup(portalData, phoneIds);
      if (sameGroup === undefined) {
        return sameGroup;
      } else {
        return !sameGroup;
      }
    }
  } catch (e) {
    return undefined;
  }
};

const onePhoneGroupSelected = (portalData, filter) => {
  try {
    if (filter.phoneVariantId.length === 1 || filter.phoneId.length === 1) {
      return true;
    } else {
      const phoneIdsOfSame = phoneIdsOfSameGroup(portalData, filter.phoneId);
      const phoneVariantIdsOfSame = phoneVariantIdsOfSamePhoneGroup(
        portalData,
        filter.phoneVariantId
      );
      if (phoneIdsOfSame === undefined || phoneVariantIdsOfSame === undefined) {
        return undefined;
      } else {
        return phoneIdsOfSame || phoneVariantIdsOfSame;
      }
    }
  } catch (e) {
    return undefined;
  }
};

const onePhoneSelected = (portalData, filter) => {
  try {
    if (filter.phoneVariantId.length === 1 || filter.phoneId.length === 1) {
      return true;
    } else {
      return phoneVariantIdsOfSamePhone(portalData, filter.phoneVariantId);
    }
  } catch (e) {
    return undefined;
  }
};

const phoneFromFilterPhoneOrPhoneVariant = (portalData, filter) => {
  try {
    if (filter.phoneId.length >= 1) {
      return pickPortalDataEntity(portalData, "phone", filter.phoneId);
    } else if (filter.phoneVariantId.length >= 1) {
      const phoneVariant = findPortalDataEntity(
        portalData,
        "phoneVariant",
        filter.phoneVariantId[0]
      );
      return findPortalDataEntity(portalData, "phone", phoneVariant.phone.id);
    }
  } catch (e) {
    return undefined;
  }
};

const phoneVariantsFromPhone = (portalData, offerType, phone) => {
  try {
    const offerTypePhoneVariantIds =
      portalData.entities.phoneVariants[`${offerType}Ids`];
    let phoneVariants = [];

    for (const phoneVariant of phone.phoneVariants) {
      if (offerTypePhoneVariantIds.indexOf(phoneVariant.id) >= 0) {
        const completePhoneVariant = findPortalDataEntity(
          portalData,
          "phoneVariant",
          phoneVariant.id
        );
        phoneVariants.push(completePhoneVariant);
      }
    }

    return phoneVariants;
  } catch (e) {
    return undefined;
  }
};

const selectedMainEntity = (offerType, filter) => {
  try {
    if (offerType === OFFER_TYPES.simOnly) {
      return MAIN_SELECTION_ENTITIES.simOnly;
    } else if (filter.phoneVariantId.length === 1) {
      return MAIN_SELECTION_ENTITIES.singlePhoneVariant;
    } else if (filter.phoneId.length === 1) {
      return MAIN_SELECTION_ENTITIES.singlePhone;
    } else if (filter.brandId.length === 1) {
      return MAIN_SELECTION_ENTITIES.singleBrand;
    } else {
      return MAIN_SELECTION_ENTITIES.multiPhone;
    }
  } catch (e) {
    return MAIN_SELECTION_ENTITIES.unknown;
  }
};

export {
  allPhonesByGroup,
  bestPhoneFromPhoneGroup,
  multiPhoneGroupsForOfferType,
  onePhoneGroupSelected,
  onePhoneSelected,
  phoneFromFilterPhoneOrPhoneVariant,
  phoneVariantsFromPhone,
  selectedMainEntity
};
